import { MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { View } from 'react-native'

// Hysteresis band: collapse once scrolled past COLLAPSE_AT, only re-expand after
// scrolling back above EXPAND_AT. The gap between them stops sub-pixel scroll
// wobble near the boundary (common with trackpads) from flipping the state.
const COLLAPSE_AT = 48
const EXPAND_AT = 16
// After a toggle, ignore scroll for a beat so any layout shift
// can't immediately reverse the state and start a flicker loop.
const TOGGLE_COOLDOWN_MS = 200
// Must match the `padding-top` transition duration on ClientProfileHeaderBar's container.
const HEADER_TRANSITION_MS = 150
const TRANSITION_SETTLE_GRACE_MS = 50

export const CLIENT_PROFILE_HEADER_BOTTOM_CSS_VAR = '--client-profile-header-bottom'

export const PAGE_SCROLLBAR_WIDTH_CSS_VAR = '--page-scrollbar-width'

// Writes a px-valued custom property to the document root, skipping the write when the
// value is unchanged.
const publishCssVar = (cssVar: string, value: number, lastRef: MutableRefObject<string | null>) => {
  const next = `${value}px`
  if (next === lastRef.current) {
    return
  }
  lastRef.current = next
  document.documentElement.style.setProperty(cssVar, next)
}

const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el = node?.parentElement ?? null
  while (el) {
    const overflowY = getComputedStyle(el).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return el
    }
    el = el.parentElement
  }
  return null
}

// Unsupported properties read as undefined or '' rather than 'none' (e.g. unprefixed
// `backdrop-filter` before Safari 18), so treat those as unset too.
const isSet = (value: string | undefined) => !!value && value !== 'none'

// `position: fixed` resolves against the viewport unless an ancestor establishes a containing
// block for fixed descendants (the providerpayments app shell does, with
// `transform-style: preserve-3d`). Returns that ancestor, or null for the viewport.
const findFixedContainingBlock = (node: HTMLElement | null): HTMLElement | null => {
  let el = node
  while (el && el !== document.documentElement) {
    const style = getComputedStyle(el)
    if (
      style.transform !== 'none' ||
      style.perspective !== 'none' ||
      style.filter !== 'none' ||
      isSet(style.backdropFilter) ||
      isSet(style.getPropertyValue('-webkit-backdrop-filter')) ||
      style.transformStyle === 'preserve-3d' ||
      /paint|layout|strict|content/.test(style.contain) ||
      /transform|perspective|filter/.test(style.willChange)
    ) {
      return el
    }
    el = el.parentElement
  }
  return null
}

/**
 * Drives the collapsed state of the client profile header on scroll.
 *
 * The returned ref is attached to the header's root view and used to locate the
 * page's scroll container (nearest ancestor with overflow auto/scroll). We watch
 * that container's scroll offset and apply hysteresis + a post-toggle cooldown so
 * the header collapses/expands cleanly without flickering at the threshold.
 *
 * The header's live bottom edge (relative to the containing block that `position: fixed`
 * descendants of the page resolve against) is also published to the document
 * root as the `CLIENT_PROFILE_HEADER_BOTTOM_CSS_VAR` custom property, so fixed layout
 * that must sit below the header (e.g. the client overview side panel) can track it in
 * CSS wherever the header sits: below app bars, the video session bar, banners, or
 * alerts. That write is synchronous with the measurement, avoiding the render-cycle lag
 * a React state round trip would introduce during the collapse/expand transition.
 *
 * The hook also reports the height removed by the collapse. The shared client-details
 * layout renders that amount after its content so changing the sticky header's height
 * does not reduce the page's scroll range. Web-only; without a DOM it never collapses.
 *
 * `containerRef` is a callback ref (not a ref object): the header can mount after the
 * hook first runs — e.g. it renders `null` while client data loads, then mounts once the
 * data arrives — so we key the effect on the resolved node to attach the listener whenever
 * the header actually mounts, regardless of timing.
 */
export const useClientProfileHeaderCollapsed = () => {
  const [containerNode, setContainerNode] = useState<HTMLElement | null>(null)
  const collapsedRef = useRef(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [compensationHeight, setCompensationHeight] = useState(0)
  const expandedHeightRef = useRef(0)
  const scrollParentRef = useRef<HTMLElement | null>(null)
  const offsetToRestoreRef = useRef<number | null>(null)
  const lastBottomRef = useRef<string | null>(null)
  const lastScrollbarWidthRef = useRef<string | null>(null)
  const transitionInFlightRef = useRef(false)

  const containerRef = useCallback((node: View | null) => {
    setContainerNode((node as unknown as HTMLElement | null) ?? null)
  }, [])

  // A collapsing header can make the page shorter than its current scroll offset,
  // causing the browser to clamp scrollTop. Once the compensating spacer is in the
  // layout, restore the captured offset only when that clamp actually happened.
  // An unconditional write would rewind and cancel scrolling that advanced while
  // the header was resizing (for example, a native smooth scroll). A clamp leaves the offset
  // at the bottom of the range the page had before the spacer; anywhere above that means the
  // user scrolled back up while the header collapsed, which must not be rewound either.
  useLayoutEffect(() => {
    const offsetToRestore = offsetToRestoreRef.current
    if (!isCollapsed || compensationHeight <= 0 || offsetToRestore === null) {
      return
    }

    const scroller = scrollParentRef.current
    const currentOffset = scroller ? scroller.scrollTop : window.scrollY
    const scrollHeight = scroller ? scroller.scrollHeight : document.documentElement.scrollHeight
    const clientHeight = scroller ? scroller.clientHeight : window.innerHeight
    const clampedOffset = scrollHeight - compensationHeight - clientHeight
    if (currentOffset < offsetToRestore && currentOffset >= clampedOffset - 1) {
      if (scroller) {
        scroller.scrollTop = offsetToRestore
      } else {
        window.scrollTo(window.scrollX, offsetToRestore)
      }
    }
    offsetToRestoreRef.current = null
  }, [compensationHeight, isCollapsed])

  // Measures the header's own position and height independently of scroll tracking, so the
  // published CSS variable stays accurate even before any scroll listener has fired.
  useLayoutEffect(() => {
    if (!containerNode || typeof window === 'undefined') {
      return
    }
    const scroller = findScrollParent(containerNode)
    // Fixed layout below the header renders inside the scroller, so its `top` resolves against
    // this. Publishing relative to it keeps the value right even when the document itself is
    // scrolled, which moves the header and the containing block together.
    const fixedContainingBlock = findFixedContainingBlock(scroller ?? containerNode.parentElement)
    const publishBottom = (bottom: number) => {
      const containingBlockTop = fixedContainingBlock?.getBoundingClientRect().top ?? 0
      publishCssVar(CLIENT_PROFILE_HEADER_BOTTOM_CSS_VAR, Math.max(0, bottom - containingBlockTop), lastBottomRef)
    }
    const applyHeight = (currentHeight: number) => {
      if (!collapsedRef.current) {
        // Grow the expanded baseline toward the live height, but never past it while a
        // transition is in flight: the observer sees every intermediate height as the
        // header animates open, and taking those verbatim would stomp the real expanded
        // height and under-compensate the next collapse.
        expandedHeightRef.current = Math.max(expandedHeightRef.current, currentHeight)
        if (!transitionInFlightRef.current) {
          expandedHeightRef.current = currentHeight
        }
      }
      // Derived from the baseline rather than gated on `collapsedRef`, which flips at
      // toggle time — before the transition runs.
      setCompensationHeight(Math.round(Math.max(0, expandedHeightRef.current - currentHeight)))
    }
    const measure = () => {
      const { bottom, height } = containerNode.getBoundingClientRect()
      publishBottom(bottom)
      applyHeight(height)
    }
    const measureBottom = () => publishBottom(containerNode.getBoundingClientRect().bottom)

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(containerNode)
    measure()

    // The header's bottom edge also moves without the header resizing. Scrolling moves it
    // until it sticks, and a stuck header moves when its sticky `top` changes. Banners and
    // alerts inside the scroller appear, disappear, or rewrap; they all live directly inside
    // it, so watching its direct children catches them. Border-box, because a content-box
    // observer misses padding-only size changes.
    const scrollTarget: HTMLElement | Window = scroller ?? window
    scrollTarget.addEventListener('scroll', measureBottom, { passive: true })
    const layoutObserver = new ResizeObserver(measureBottom)
    const observeLayout = (el: Element) => layoutObserver.observe(el, { box: 'border-box' })
    // Scroller children added or removed, and header or sibling style changes. Attribute
    // records carry no added/removed nodes, so they only trigger the re-measure.
    const domObserver = new MutationObserver((records) => {
      records.forEach(({ addedNodes, removedNodes }) => {
        addedNodes.forEach((node) => node instanceof Element && observeLayout(node))
        removedNodes.forEach((node) => node instanceof Element && layoutObserver.unobserve(node))
      })
      measureBottom()
    })
    // The sticky `top` can change without the header resizing or anything else in the layout
    // changing size (the Join Session banner and the header's offset update on separate timers).
    domObserver.observe(containerNode, { attributes: true, attributeFilter: ['style', 'class'] })
    if (scroller) {
      observeLayout(scroller)
      Array.from(scroller.children).forEach(observeLayout)
      domObserver.observe(scroller, { childList: true })
      Array.from(scroller.parentElement?.children ?? [])
        .filter((sibling) => sibling !== scroller)
        .forEach((sibling) => {
          observeLayout(sibling)
          domObserver.observe(sibling, { attributes: true, attributeFilter: ['style', 'class'] })
        })
    }

    // The collapse/expand animates `padding-top`, which a ResizeObserver does not report:
    // it watches the content box, and padding lives outside it. Only the conditionally
    // rendered children trip the observer, and they toggle instantly at the start of the
    // transition — so without this the published bottom edge would settle short by the padding
    // delta and leave the panel overlapping the header.
    let rafId = 0
    let stopAt = 0
    const sampleUntilSettled = () => {
      if (performance.now() < stopAt) {
        measure()
        rafId = requestAnimationFrame(sampleUntilSettled)
      } else {
        rafId = 0
        transitionInFlightRef.current = false
        measure()
      }
    }
    const onTransitionRun = (event: TransitionEvent) => {
      if (event.target !== containerNode || event.propertyName !== 'padding-top') {
        return
      }
      transitionInFlightRef.current = true
      stopAt = performance.now() + HEADER_TRANSITION_MS + TRANSITION_SETTLE_GRACE_MS
      if (!rafId) {
        rafId = requestAnimationFrame(sampleUntilSettled)
      }
    }
    // `transitionrun` fires when the transition is created, ahead of the first frame;
    // `transitionend` covers the settled value in case a frame is dropped at the end.
    containerNode.addEventListener('transitionrun', onTransitionRun)
    containerNode.addEventListener('transitionend', measure)

    return () => {
      resizeObserver.disconnect()
      layoutObserver.disconnect()
      domObserver.disconnect()
      scrollTarget.removeEventListener('scroll', measureBottom)
      containerNode.removeEventListener('transitionrun', onTransitionRun)
      containerNode.removeEventListener('transitionend', measure)
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      transitionInFlightRef.current = false
      document.documentElement.style.removeProperty(CLIENT_PROFILE_HEADER_BOTTOM_CSS_VAR)
      lastBottomRef.current = null
    }
  }, [containerNode])

  useEffect(() => {
    if (!containerNode || typeof window === 'undefined') {
      return
    }
    const scroller = findScrollParent(containerNode)
    scrollParentRef.current = scroller
    const target: HTMLElement | Window = scroller ?? window

    // Disable scroll anchoring on the scroll container. Without this, when the
    // header changes height the browser adjusts scrollTop to keep the visible
    // content stable — which feeds the height change back into scrollTop and can
    // loop forever near the top.
    const anchorEl = scroller ?? document.documentElement
    const previousAnchor = anchorEl.style.getPropertyValue('overflow-anchor')
    anchorEl.style.setProperty('overflow-anchor', 'none')

    const publishScrollbarWidth = () => {
      const scrollbarWidth = scroller ? scroller.offsetWidth - scroller.clientWidth : 0
      publishCssVar(PAGE_SCROLLBAR_WIDTH_CSS_VAR, scrollbarWidth, lastScrollbarWidthRef)
    }
    const scrollbarResizeObserver = new ResizeObserver(publishScrollbarWidth)
    if (scroller) {
      scrollbarResizeObserver.observe(scroller)
    }
    publishScrollbarWidth()

    let ticking = false
    let lastToggle = 0
    let trailingTimer: ReturnType<typeof setTimeout> | undefined
    const readOffset = () => (scroller ? scroller.scrollTop : window.scrollY)

    const evaluate = () => {
      const y = readOffset()
      const prev = collapsedRef.current
      let next = prev
      if (!prev && y > COLLAPSE_AT) {
        next = true
      } else if (prev && y < EXPAND_AT) {
        next = false
      }
      if (next === prev) {
        return
      }
      // Defer toggles that land inside the cooldown until it ends, re-reading the
      // (by then settled) position — otherwise a dropped toggle leaves the header
      // stuck if scrolling has stopped and no further scroll events arrive.
      const remaining = TOGGLE_COOLDOWN_MS - (Date.now() - lastToggle)
      if (remaining > 0) {
        if (!trailingTimer) {
          trailingTimer = setTimeout(() => {
            trailingTimer = undefined
            evaluate()
          }, remaining)
        }
        return
      }
      if (next) {
        expandedHeightRef.current = containerNode.getBoundingClientRect().height
        offsetToRestoreRef.current = y
      } else {
        offsetToRestoreRef.current = null
      }
      collapsedRef.current = next
      lastToggle = Date.now()
      setIsCollapsed(next)
    }

    const onScroll = () => {
      if (ticking) {
        return
      }
      ticking = true
      window.requestAnimationFrame(() => {
        ticking = false
        evaluate()
      })
    }

    target.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => {
      target.removeEventListener('scroll', onScroll)
      scrollbarResizeObserver.disconnect()
      document.documentElement.style.removeProperty(PAGE_SCROLLBAR_WIDTH_CSS_VAR)
      lastScrollbarWidthRef.current = null
      scrollParentRef.current = null
      offsetToRestoreRef.current = null
      if (trailingTimer) {
        clearTimeout(trailingTimer)
      }
      if (previousAnchor) {
        anchorEl.style.setProperty('overflow-anchor', previousAnchor)
      } else {
        anchorEl.style.removeProperty('overflow-anchor')
      }
    }
  }, [containerNode])

  return { containerRef, isCollapsed, compensationHeight }
}
